/* ============================================================================
 * Dreame HK PDP content tabs (Convx, 2026-10-26), #12
 *
 * Markup: sections/convx-pdp-tabs.liquid (start marker, tab bar, warranty and
 * shipping panels) and sections/convx-pdp-tabs-end.liquid (end marker and the
 * 查看更多 / 收起 toggle). Styling and the state classes: assets/convx-pdp-tabs.css.
 *
 * The description run is the .shopify-section wrappers strictly between the
 * wrapper holding [data-cx-tabs] and the wrapper holding [data-cx-tabs-end].
 * They are never moved or wrapped (that would reload the YouTube iframe, re-run
 * custom element callbacks and break section selection in the theme editor);
 * this file only toggles classes on them.
 *
 *   1. Collapse: peek of one description section (two when the first is under
 *      360px), clipped to --cx-peek, white fade only when the clipped one
 *      really overflows, 查看更多 / 收起 toggle.
 *   2. Tabs: WAI-ARIA tabs pattern (arrows, Home, End), 保養及退換 and 送貨詳情
 *      panels with their own 640px clamp, and a 產品規格 tab that shows the run's
 *      product-specification and product-bot-compare sections in full.
 *   3. window.ConvxTabs.reveal(el): called by convx-pdp.js before every
 *      anchor-nav scroll (it returns the node to scroll to: the KOL row
 *      when given the end section, null otherwise); also runs for a
 *      location.hash inside the run.
 *   4. Shipping panel: server-rendered when a page with content is picked in
 *      the theme settings, otherwise /pages/shipping-policy is fetched once
 *      (prefetch when the tab bar scrolls into view, or on first tab click).
 *   5. KOL row (Instagram embeds, YouTube facades, Shopify video) and the
 *      removal of the 「KOL 推薦」 nav item on templates without one.
 *   6. GA4 via window.trackCustomEvent: pdp_tab_click, pdp_desc_toggle,
 *      pdp_ugc_open { ugc_position, ugc_source }.
 *
 * ES5-safe, no dependencies, deferred.
 * ==========================================================================*/
(function () {
  'use strict';

  var PEEK_SPLIT = 300;      // first section shorter than this: peek a second one
  var POLICY_CLAMP = 640;    // policy panels collapse to this height
  var root = document.documentElement;
  var S = { specs: [] };    // state, rebuilt by init()
  var bound = { global: false };

  /* ---------------------------------------------------------------- utils */
  function track(name, params) {
    try {
      if (typeof window.trackCustomEvent === 'function') window.trackCustomEvent(name, params);
    } catch (e) { /* analytics never breaks the page */ }
  }

  function wrapperOf(node) {
    return node && node.closest ? node.closest('.shopify-section') : null;
  }

  function cssPx(name, fallback) {
    var v = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  function headerOffset() {
    var h = document.querySelector('sticky-header header.header, .header-wrapper header.header');
    if (!h) return 0;
    return Math.max(0, h.getBoundingClientRect().bottom);
  }

  function navHeight() {
    var n = document.querySelector('[data-cx-anchor].is-pinned');
    return n ? n.getBoundingClientRect().height : 0;
  }

  function wantedTop(target) {
    var y = window.pageYOffset + target.getBoundingClientRect().top;
    return Math.max(0, y - (headerOffset() + navHeight() + 8));
  }

  // Same section-key fallback as convx-pdp.js: ids embed the template id, which
  // changes when the theme is duplicated, the key after "__" does not.
  function resolveTarget(id) {
    if (!id) return null;
    var exact = document.getElementById(id);
    if (exact) return exact;
    var cut = id.lastIndexOf('__');
    if (cut === -1) return null;
    var key = id.slice(cut + 2);
    if (!key) return null;
    var safe = key.replace(/["\\]/g, '\\$&');
    return document.querySelector('.shopify-section[id$="__' + safe + '"]') ||
           document.querySelector('[id$="__' + safe + '"]');
  }

  /* ------------------------------------------------------------- run model */
  function findRun() {
    var startW = wrapperOf(document.querySelector('[data-cx-tabs]'));
    var endW = wrapperOf(document.querySelector('[data-cx-tabs-end]'));
    if (!startW || !endW) return null;
    var secs = [];
    var n = startW.nextElementSibling;
    while (n && n !== endW) {
      if (n.classList.contains('shopify-section')) secs.push(n);
      n = n.nextElementSibling;
    }
    if (n !== endW) return null;   // end marker is not a later sibling of the start marker
    return { startW: startW, endW: endW, secs: secs };
  }

  function inRun(el) {
    if (!S.run || !el) return null;
    for (var i = 0; i < S.run.secs.length; i++) {
      if (S.run.secs[i] === el || S.run.secs[i].contains(el)) return S.run.secs[i];
    }
    return null;
  }

  function isAlt() { return root.classList.contains('cx-tab-alt'); }
  function isSpecs() { return root.classList.contains('cx-tab-specs'); }
  // true when the run section is one of the tab host's specification sections
  function isSpecSec(sec) { return !!sec && S.specs.indexOf(sec) > -1; }
  function isOpen() { return root.classList.contains('cx-desc-open'); }

  /* ------------------------------------------------------- collapse layout */
  function clearClasses(s) {
    s.classList.remove('cx-desc-hidden', 'cx-desc-clip', 'cx-desc-fade');
    s.style.removeProperty('--cx-clip');
  }

  // Measure every section at its natural height, then assign the collapsed
  // classes. Runs synchronously inside one task, so the intermediate "all
  // visible" state is never painted.
  function layout() {
    if (!S.run || S.design) return;
    var all = S.run.secs, secs = [], i, h = [];
    root.classList.add('cx-tabs-ready');
    S.peeked = null;
    for (i = 0; i < all.length; i++) {
      all[i].classList.add('cx-desc-sec');
      clearClasses(all[i]);
      // specification and compare sections belong to the 產品規格 tab: they never take part in the peek
      if (isSpecSec(all[i])) all[i].classList.add('cx-spec-sec'); else secs.push(all[i]);
    }
    if (isAlt() || isSpecs()) { setToggleVisible(false); return; }   // hidden by the html class; measured on return
    for (i = 0; i < secs.length; i++) h.push(secs[i].getBoundingClientRect().height);

    if (isOpen()) { setToggleVisible(true); S.nat = h; return; }

    var peek = cssPx('--cx-peek', 600);
    var cand = [];   // up to two sections with real height, the peek
    for (i = 0; i < secs.length && cand.length < 2; i++) {
      if (h[i] > 0.5) {
        cand.push(i);
        if (h[i] >= PEEK_SPLIT) break;   // only a short first section earns a second one
      }
    }
    if (!cand.length) { setToggleVisible(false); S.nat = h; return; }

    var last = cand[cand.length - 1];
    var used = 0, c;
    for (c = 0; c < cand.length - 1; c++) used += h[cand[c]];
    var room = Math.max(0, peek - used);
    var overflow = h[last] > room + 1;
    var hidden = false;
    for (i = last + 1; i < secs.length; i++) {
      if (h[i] > 0.5) hidden = true;
      secs[i].classList.add('cx-desc-hidden');
    }
    if (overflow) {
      secs[last].classList.add('cx-desc-clip', 'cx-desc-fade');
      secs[last].style.setProperty('--cx-clip', Math.round(room) + 'px');
    }
    S.peeked = cand.map(function (k) { return secs[k]; });
    S.scr = S.peeked.map(function (el) { return el.scrollHeight; });
    S.nat = h;
    setToggleVisible(hidden || overflow);
    observePeek();
  }

  function setToggleVisible(on) {
    if (!S.endWrap) return;
    if (on) S.endWrap.removeAttribute('hidden'); else S.endWrap.setAttribute('hidden', '');
  }

  var ro = null, roQueued = false;
  function observePeek() {
    if (typeof ResizeObserver === 'undefined') return;
    if (ro) ro.disconnect();
    if (isOpen() || isAlt() || isSpecs() || !S.peeked) return;
    ro = new ResizeObserver(function () {
      if (roQueued) return;
      roQueued = true;
      window.requestAnimationFrame(function () {
        roQueued = false;
        if (isOpen() || isAlt() || isSpecs() || !S.peeked) return;
        // only re-layout when a peeked section's content really changed height
        // (scrollHeight against scrollHeight: the same measure both times)
        var changed = false;
        for (var i = 0; i < S.peeked.length; i++) {
          if (Math.abs(S.peeked[i].scrollHeight - S.scr[i]) > 2) changed = true;
        }
        if (changed) layout();
      });
    });
    for (var i = 0; i < S.peeked.length; i++) {
      ro.observe(S.peeked[i]);
      for (var k = 0; k < S.peeked[i].children.length; k++) ro.observe(S.peeked[i].children[k]);
    }
  }

  /* ----------------------------------------------------- expand / collapse */
  function setToggleLabel() {
    if (!S.toggle) return;
    var open = isOpen();
    S.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    var l = S.toggle.querySelector('.cx-desc-toggle__label');
    if (l) l.textContent = open ? '收起' : '查看更多';
  }

  function expand(user) {
    if (S.design) return;
    if (!isOpen()) {
      root.classList.add('cx-desc-open');
      if (ro) ro.disconnect();
      setToggleLabel();
      layout();   // clears the collapsed classes; keeps the toggle visible
    }
    if (user) track('pdp_desc_toggle', { desc_action: 'expand', desc_panel: 'description' });
  }

  function scrollToTabs() {
    var bar = S.bar;
    if (!bar) return;
    window.scrollTo(0, wantedTop(bar));
    // header and pinned nav can settle after the page got shorter: correct once
    window.requestAnimationFrame(function () {
      var want = wantedTop(bar);
      if (Math.abs(want - window.pageYOffset) > 2) window.scrollTo(0, want);
    });
  }

  function collapse(user) {
    if (S.design) return;
    root.classList.remove('cx-desc-open');
    setToggleLabel();
    layout();
    scrollToTabs();
    if (user) track('pdp_desc_toggle', { desc_action: 'collapse', desc_panel: 'description' });
  }

  /* ------------------------------------------------------------------ tabs */
  function tabButtons() {
    return S.root ? Array.prototype.slice.call(S.root.querySelectorAll('[data-cx-tab]')) : [];
  }

  function currentTab() {
    var tabs = tabButtons();
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('aria-selected') === 'true') return tabs[i].getAttribute('data-cx-tab');
    }
    return 'description';
  }

  function layoutPolicy(panel) {
    var wrap = panel.querySelector('[data-cx-policy]');
    var btn = panel.querySelector('[data-cx-policy-toggle]');
    if (!wrap || !btn) return;
    var wasOpen = btn.getAttribute('aria-expanded') === 'true';
    wrap.classList.remove('is-clamped');
    var need = wrap.getBoundingClientRect().height > POLICY_CLAMP;
    if (!need) { btn.setAttribute('hidden', ''); return; }
    btn.removeAttribute('hidden');
    if (!wasOpen) wrap.classList.add('is-clamped');
  }

  function select(name, opts) {
    opts = opts || {};
    var tabs = tabButtons(), i, found = false;
    for (i = 0; i < tabs.length; i++) if (tabs[i].getAttribute('data-cx-tab') === name) found = true;
    if (!found) return;
    var prev = currentTab();
    for (i = 0; i < tabs.length; i++) {
      var on = tabs[i].getAttribute('data-cx-tab') === name;
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].setAttribute('tabindex', on ? '0' : '-1');
      if (on && opts.focus) tabs[i].focus();
    }
    var panels = S.root.querySelectorAll('[data-cx-panel]');
    for (i = 0; i < panels.length; i++) {
      var show = panels[i].getAttribute('data-cx-panel') === name;
      if (show) panels[i].removeAttribute('hidden'); else panels[i].setAttribute('hidden', '');
    }
    if (name === 'shipping') loadShipping();
    var alt = name === 'warranty' || name === 'shipping';
    root.classList.toggle('cx-tab-alt', alt);
    root.classList.toggle('cx-tab-specs', name === 'specs');
    S.root.setAttribute('data-cx-active', name);
    if (alt) {
      var p = S.root.querySelector('[data-cx-panel="' + name + '"]');
      if (p) layoutPolicy(p);
    } else {
      layout();   // sections were display:none, heights may have changed
    }
    if (name !== prev && !opts.silent) track('pdp_tab_click', { tab_name: name });
  }

  function onTabKey(e) {
    var tabs = tabButtons();
    var idx = tabs.indexOf(document.activeElement);
    if (idx < 0) return;
    var to = -1;
    if (e.key === 'ArrowRight' || e.key === 'Right') to = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'Left') to = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') to = 0;
    else if (e.key === 'End') to = tabs.length - 1;
    if (to < 0) return;
    e.preventDefault();
    select(tabs[to].getAttribute('data-cx-tab'), { focus: true });
  }

  /* --------------------------------------------------------------- policy */
  function onPolicyToggle(btn) {
    var panel = btn.closest('[data-cx-panel]');
    if (!panel) return;
    var wrap = panel.querySelector('[data-cx-policy]');
    var name = panel.getAttribute('data-cx-panel');
    var open = btn.getAttribute('aria-expanded') === 'true';
    var l = btn.querySelector('.cx-desc-toggle__label');
    if (open) {
      btn.setAttribute('aria-expanded', 'false');
      wrap.classList.add('is-clamped');
      if (l) l.textContent = btn.getAttribute('data-label-open');
      scrollToTabs();
    } else {
      btn.setAttribute('aria-expanded', 'true');
      wrap.classList.remove('is-clamped');
      if (l) l.textContent = btn.getAttribute('data-label-close');
    }
    track('pdp_desc_toggle', { desc_action: open ? 'collapse' : 'expand', desc_panel: name });
  }

  /* --------------------------------------------------------- shipping page */
  // /pages/shipping-policy keeps its body in rich-text sections of its own
  // template (page.content is empty), so it is fetched once and the text blocks
  // are copied in. No section keys are hardcoded: every .rich-text__blocks in
  // #MainContent, in order, minus the .rich-text__caption.
  var ship = { state: 'idle' };

  function shipPanel() {
    return S.root ? S.root.querySelector('[data-cx-panel="shipping"]') : null;
  }

  function cleanNode(node) {
    // the copied node itself carries the page's own classes (rich-text__heading rte h0 ...)
    for (var r = node.attributes.length - 1; r >= 0; r--) node.removeAttribute(node.attributes[r].name);
    var bad = node.querySelectorAll('script, style, link, iframe, object, embed, form, input, button');
    for (var i = 0; i < bad.length; i++) bad[i].parentNode.removeChild(bad[i]);
    var all = node.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var attrs = all[j].attributes;
      for (var k = attrs.length - 1; k >= 0; k--) {
        var n = attrs[k].name;
        if (n === 'href' || n === 'colspan' || n === 'rowspan') {
          if (n === 'href' && /^\s*javascript:/i.test(attrs[k].value)) all[j].removeAttribute(n);
          continue;
        }
        all[j].removeAttribute(n);
      }
    }
  }

  function shipDone(panel, ok, nodes) {
    var wrap = panel.querySelector('[data-cx-policy]');
    if (!wrap) return;
    wrap.removeAttribute('data-cx-ship-src');
    while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    if (ok) {
      for (var i = 0; i < nodes.length; i++) wrap.appendChild(nodes[i]);
      ship.state = 'done';
    } else {
      var p = document.createElement('p');
      var a = document.createElement('a');
      a.href = '/pages/shipping-policy';
      a.textContent = '查看送貨政策';
      p.appendChild(a);
      wrap.appendChild(p);
      ship.state = 'failed';
    }
    if (!panel.hasAttribute('hidden')) layoutPolicy(panel);
  }

  function loadShipping() {
    var panel = shipPanel();
    if (!panel || ship.state !== 'idle') return;
    var wrap = panel.querySelector('[data-cx-ship-src]');
    if (!wrap) return;
    ship.state = 'loading';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', wrap.getAttribute('data-cx-ship-src'), true);
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;
      var nodes = [];
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          var doc = new DOMParser().parseFromString(xhr.responseText, 'text/html');
          var main = doc.getElementById('MainContent');
          var blocks = main ? main.querySelectorAll('.rich-text__blocks') : [];
          for (var i = 0; i < blocks.length; i++) {
            var kids = blocks[i].children;
            for (var k = 0; k < kids.length; k++) {
              if (kids[k].classList.contains('rich-text__caption')) continue;
              if (!(kids[k].textContent || '').replace(/\s+/g, '')) continue;
              var copy = document.importNode(kids[k], true);
              cleanNode(copy);
              nodes.push(copy);
            }
          }
        }
      } catch (e) { nodes = []; }
      shipDone(panel, nodes.length > 0, nodes);
    };
    try { xhr.send(); } catch (e) { shipDone(panel, false, []); }
  }

  function prefetchShipping() {
    if (!shipPanel() || !shipPanel().querySelector('[data-cx-ship-src]')) return;
    if (typeof IntersectionObserver === 'undefined' || !S.bar) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); loadShipping(); return; }
      }
    }, { rootMargin: '200px 0px' });
    io.observe(S.bar);
  }

  /* ------------------------------------------------------------ reveal API */
  // If el is a description section or inside one: select 產品介紹 and expand
  // (no scrolling here: the caller scrolls). Returns the node the caller should
  // scroll to instead of el, or null to keep el: given the end section it
  // returns the KOL row, so the 「KOL 推薦」 anchor lands on the row and
  // not on the toggle.
  function reveal(el) {
    if (!S.run || S.design || !el) return null;
    if (S.run.endW === el || S.run.endW.contains(el)) {
      return S.run.endW.querySelector('[data-cx-ugc]') || null;
    }
    var sec = inRun(el);
    if (!sec) return null;
    // a specification or compare section lives under 產品規格: select that tab (no expand) and keep el
    if (isSpecSec(sec) && S.root.querySelector('[data-cx-tab="specs"]')) {
      if (!isSpecs()) select('specs', { silent: true });
      return el;
    }
    if (isAlt() || isSpecs()) select('description', { silent: true });
    if (!isOpen()) expand(false);
    return null;
  }

  function scrollToHash() {
    var id = '';
    try { id = decodeURIComponent((location.hash || '').slice(1)); } catch (e) { id = (location.hash || '').slice(1); }
    if (!id) return;
    var t = resolveTarget(id);
    if (!t || !S.run) return;
    // targets above the tab bar are left to the browser; the run is revealed; everything below is
    // re-scrolled because collapsing the run moved it
    if (S.run.startW.compareDocumentPosition(t) & 2) return;   // t precedes the tab host
    reveal(t);
    var tries = 0;
    (function go() {
      window.scrollTo(0, wantedTop(t));
      // imagery below the fold changes heights while it loads: settle a few times
      if (++tries < 4) setTimeout(go, tries * 350);
    })();
  }

  /* ------------------------------------------------------------- KOL row */
  // Markup: sections/convx-pdp-tabs-end.liquid ([data-cx-ugc]). Cards are
  // Instagram /embed/ iframes (height from Instagram's MEASURE postMessage, a
  // fallback until it arrives; never cropped or overlaid), YouTube thumbnail
  // facades that swap to a youtube-nocookie iframe on tap, or Shopify videos.
  function kolRoot() {
    return S.run ? S.run.endW.querySelector('[data-cx-ugc]') : null;
  }

  function kolCards() {
    var r = kolRoot();
    return r ? Array.prototype.slice.call(r.querySelectorAll('.cx-kol__card')) : [];
  }

  function kolPos(card) {
    return kolCards().indexOf(card) + 1;
  }

  function igFallbackHeight(frame) {
    var w = frame.getBoundingClientRect().width || frame.parentNode.clientWidth || 380;
    return Math.round(w * 1.25 + 230);
  }

  function onMessage(e) {
    if (!/(^|\.)instagram\.com$/.test((e.origin || '').replace(/^https?:\/\//, ''))) return;
    var d = e.data;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (err) { return; } }
    if (!d || d.type !== 'MEASURE' || !d.details || !d.details.height) return;
    var frames = document.querySelectorAll('.cx-kol__embed');
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].contentWindow === e.source) {
        frames[i].style.height = Math.ceil(d.details.height) + 'px';
        var row = frames[i].closest('[data-cx-row]');
        if (row) updateNav(row);
        return;
      }
    }
  }

  function updateNav(row) {
    var track = row.querySelector('.cx-kol__track');
    var prev = row.querySelector('[data-cx-nav="-1"]');
    var next = row.querySelector('[data-cx-nav="1"]');
    if (!track || !prev || !next) return;
    var over = track.scrollWidth > track.clientWidth + 2;
    if (!over) { prev.setAttribute('hidden', ''); next.setAttribute('hidden', ''); return; }
    prev.removeAttribute('hidden'); next.removeAttribute('hidden');
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  function youtubeSwap(card) {
    var id = card.getAttribute('data-cx-yt');
    var facade = card.querySelector('.cx-kol__facade');
    if (!id || !facade) return;
    var start = parseInt(card.getAttribute('data-cx-yt-start'), 10) || 0;
    var f = document.createElement('iframe');
    f.className = 'cx-kol__yt-frame';
    f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&playsinline=1&rel=0' + (start ? '&start=' + start : '');
    f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    f.setAttribute('allowfullscreen', '');
    f.setAttribute('title', 'YouTube');
    f.setAttribute('frameborder', '0');
    facade.parentNode.replaceChild(f, facade);
  }

  function initKol() {
    var root = kolRoot();
    if (!root || root.getAttribute('data-cx-kol-bound')) return;
    root.setAttribute('data-cx-kol-bound', '1');

    // Instafeed row: its posts render later, so delegate on the row
    if (root.hasAttribute('data-cx-ugc-app')) {
      root.addEventListener('click', function (e) {
        var it = e.target.closest ? e.target.closest('.instafeed-new-layout-item') : null;
        if (!it) return;
        var all = Array.prototype.slice.call(root.querySelectorAll('.instafeed-new-layout-item'));
        track('pdp_ugc_open', { ugc_position: all.indexOf(it) + 1, ugc_source: 'instafeed' });
      }, true);
      return;
    }

    // Instagram: a height before the MEASURE message arrives
    var frames = root.querySelectorAll('.cx-kol__embed');
    for (var i = 0; i < frames.length; i++) frames[i].style.height = igFallbackHeight(frames[i]) + 'px';

    // YouTube facade and video taps
    root.addEventListener('click', function (e) {
      var facade = e.target.closest ? e.target.closest('.cx-kol__facade') : null;
      if (facade) {
        var card = facade.closest('.cx-kol__card');
        track('pdp_ugc_open', { ugc_position: kolPos(card), ugc_source: 'youtube' });
        youtubeSwap(card);
        return;
      }
      var vc = e.target.closest ? e.target.closest('.cx-kol__vcard') : null;
      if (vc) { openLightbox(vc.closest('.cx-kol__card')); return; }
      var nav = e.target.closest ? e.target.closest('[data-cx-nav]') : null;
      if (nav) {
        var row = nav.closest('[data-cx-row]');
        var tr = row.querySelector('.cx-kol__track');
        var first = tr.querySelector('.cx-kol__card');
        var step = (first ? first.getBoundingClientRect().width : 380) + 16;
        tr.scrollBy({ left: step * parseInt(nav.getAttribute('data-cx-nav'), 10), behavior: 'smooth' });
      }
    });

    // video cards: keyboard open, play only while at least half in view
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var vc2 = e.target.closest ? e.target.closest('.cx-kol__vcard') : null;
      if (!vc2) return;
      e.preventDefault();
      openLightbox(vc2.closest('.cx-kol__card'));
    });
    watchVideos(root);

    // arrows only when a row overflows
    var rows = root.querySelectorAll('[data-cx-row]');
    Array.prototype.forEach.call(rows, function (row) {
      var tr = row.querySelector('.cx-kol__track');
      tr.addEventListener('scroll', function () { updateNav(row); }, { passive: true });
      updateNav(row);
    });
    window.addEventListener('resize', function () {
      Array.prototype.forEach.call(rows, updateNav);
    });
    window.addEventListener('load', function () { Array.prototype.forEach.call(rows, updateNav); });
  }

  /* ------------------------------------------------------ KOL videos */
  // Cards play muted, only while at least half in view; preload stays "none"
  // until the card is within 400px of the viewport. A tap opens the lightbox.
  function safePlay(v) {
    try { var p = v.play(); if (p && p.catch) p.catch(function () { /* autoplay refused */ }); } catch (e) { /* noop */ }
  }

  function watchVideos(root) {
    var vids = root.querySelectorAll('.cx-kol__video');
    if (!vids.length) return;
    if (typeof IntersectionObserver === 'undefined') return;
    var near = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.preload = 'metadata';
        near.unobserve(entries[i].target);
      }
    }, { rootMargin: '400px 400px' });
    var vis = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var v = entries[i].target;
        if (S.lb && !S.lb.hasAttribute('hidden')) { v.pause(); continue; }
        if (entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
          v.muted = true;
          safePlay(v);
        } else {
          v.pause();
        }
      }
    }, { threshold: [0, 0.5, 1] });
    for (var k = 0; k < vids.length; k++) { near.observe(vids[k]); vis.observe(vids[k]); }
    S.videoIO = vis;
  }

  function videoCards() {
    var r = kolRoot();
    return r ? Array.prototype.slice.call(r.querySelectorAll('.cx-kol__card--video')) : [];
  }

  var LB_CLOSE = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var LB_PREV = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6"/></svg>';
  var LB_NEXT = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>';

  function buildLightbox() {
    if (S.lb) return S.lb;
    var lb = document.createElement('div');
    lb.className = 'cx-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'KOL 影片');
    lb.setAttribute('tabindex', '-1');
    lb.setAttribute('hidden', '');
    lb.innerHTML =
      '<button type="button" class="cx-lb__btn cx-lb__close" aria-label="關閉">' + LB_CLOSE + '</button>' +
      '<button type="button" class="cx-lb__btn cx-lb__prev" aria-label="上一條影片">' + LB_PREV + '</button>' +
      '<div class="cx-lb__stage"><video class="cx-lb__video" controls playsinline loop tabindex="-1"></video></div>' +
      '<button type="button" class="cx-lb__btn cx-lb__next" aria-label="下一條影片">' + LB_NEXT + '</button>';
    document.body.appendChild(lb);
    // keys pressed inside a <video controls> never reach the page (Escape and Tab included), so the video never keeps focus
    lb.querySelector('.cx-lb__video').addEventListener('focus', function () { lb.focus({ preventScroll: true }); });
    lb.querySelector('.cx-lb__close').addEventListener('click', closeLightbox);
    lb.querySelector('.cx-lb__prev').addEventListener('click', function () { stepLightbox(-1); });
    lb.querySelector('.cx-lb__next').addEventListener('click', function () { stepLightbox(1); });
    // click on the dark backdrop closes; clicks on the video or buttons do not
    lb.addEventListener('click', function (e) { if (e.target === lb || e.target.classList.contains('cx-lb__stage')) closeLightbox(); });
    // swipe between videos
    var x0 = null, y0 = null;
    lb.addEventListener('touchstart', function (e) { if (e.touches.length === 1) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; } }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      if (x0 === null || !e.changedTouches.length) return;
      var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) stepLightbox(dx < 0 ? 1 : -1);
    }, { passive: true });
    S.lb = lb;
    return lb;
  }

  function lbFocusables() {
    var all = S.lb.querySelectorAll('button:not([disabled])');
    return Array.prototype.slice.call(all);
  }

  function onLightboxKey(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeLightbox(); return; }
    if ((e.key === ' ' || e.key === 'Spacebar' || e.key === 'k') && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
      var lv = S.lb.querySelector('.cx-lb__video');
      if (lv.paused) safePlay(lv); else lv.pause();
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.tagName !== 'VIDEO') { e.preventDefault(); stepLightbox(-1); return; }
    if (e.key === 'ArrowRight' && e.target.tagName !== 'VIDEO') { e.preventDefault(); stepLightbox(1); return; }
    if (e.key !== 'Tab') return;
    var f = lbFocusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1], a = document.activeElement;
    var outside = !S.lb.contains(a) || a === S.lb;
    if (e.shiftKey && (a === first || outside)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (a === last || outside)) { e.preventDefault(); first.focus(); }
  }

  function showLightboxCard(card) {
    var lb = S.lb, v = lb.querySelector('.cx-lb__video');
    var srcs = card.querySelectorAll('.cx-kol__video source');
    while (v.firstChild) v.removeChild(v.firstChild);
    for (var i = 0; i < srcs.length; i++) {
      var s2 = document.createElement('source');
      s2.src = srcs[i].getAttribute('src');
      s2.type = srcs[i].getAttribute('type') || '';
      v.appendChild(s2);
    }
    var poster = card.querySelector('.cx-kol__video');
    if (poster && poster.getAttribute('poster')) v.setAttribute('poster', poster.getAttribute('poster')); else v.removeAttribute('poster');
    v.muted = false;
    v.controls = true;
    v.load();
    safePlay(v);
    S.lbCard = card;
    var cards = videoCards(), idx = cards.indexOf(card);
    lb.querySelector('.cx-lb__prev').disabled = idx <= 0;
    lb.querySelector('.cx-lb__next').disabled = idx >= cards.length - 1;
    lb.setAttribute('data-cx-index', String(idx + 1));
  }

  function openLightbox(card) {
    if (!card) return;
    var lb = buildLightbox();
    S.lbReturn = document.activeElement && document.activeElement !== document.body ? document.activeElement : card.querySelector('.cx-kol__vcard');
    lb.removeAttribute('hidden');
    root.classList.add('cx-lb-open');
    // capture phase on the document: a focused <video controls> swallows Escape and Tab before they bubble
    document.addEventListener('keydown', onLightboxKey, true);
    // the cards behind stop
    var vids = document.querySelectorAll('.cx-kol__video');
    for (var i = 0; i < vids.length; i++) vids[i].pause();
    showLightboxCard(card);
    track('pdp_ugc_open', { ugc_position: kolPos(card), ugc_source: 'video' });
    var close = lb.querySelector('.cx-lb__close');
    close.focus();
  }

  function stepLightbox(d) {
    var cards = videoCards(), idx = cards.indexOf(S.lbCard) + d;
    if (idx < 0 || idx >= cards.length) return;
    showLightboxCard(cards[idx]);
  }

  function closeLightbox() {
    var lb = S.lb;
    if (!lb || lb.hasAttribute('hidden')) return;
    var v = lb.querySelector('.cx-lb__video');
    v.pause();
    while (v.firstChild) v.removeChild(v.firstChild);
    v.removeAttribute('src');
    v.load();
    lb.setAttribute('hidden', '');
    root.classList.remove('cx-lb-open');
    document.removeEventListener('keydown', onLightboxKey, true);
    var back = S.lbReturn;
    S.lbReturn = null;
    if (back && document.contains(back)) back.focus();
    // let the row's observer resume the cards that are in view
    if (S.videoIO) {
      var vids = document.querySelectorAll('.cx-kol__video');
      for (var i = 0; i < vids.length; i++) { S.videoIO.unobserve(vids[i]); S.videoIO.observe(vids[i]); }
    }
  }

  // Instagram taps happen inside a cross-origin iframe: the page only sees its
  // window lose focus to one of our embed iframes. Once per iframe.
  function onWindowBlur() {
    setTimeout(function () {
      var a = document.activeElement;
      if (a && a.tagName === 'IFRAME' && a.classList.contains('cx-kol__embed') && !a.getAttribute('data-cx-fired')) {
        a.setAttribute('data-cx-fired', '1');
        track('pdp_ugc_open', { ugc_position: kolPos(a.closest('.cx-kol__card')), ugc_source: 'instagram' });
      }
    }, 0);
  }

  // No KOL row on this template: the end section is still the target of the old
  // 「KOL 推薦」 anchor link, so drop that nav item. Safe whichever of this file
  // and convx-pdp.js runs first: both versions of the href resolve to the end
  // section, and a removed item is simply not scroll-spied or clicked.
  function kolNavItems() {
    var out = [];
    var links = document.querySelectorAll('.anchor_point_inner a[href]');
    for (var i = 0; i < links.length; i++) {
      var h = links[i].getAttribute('href') || '';
      var id = h.indexOf('#') !== -1 ? h.slice(h.indexOf('#') + 1) : '';
      var t = id ? resolveTarget(id) : null;
      if (t && (t === S.run.endW || S.run.endW.contains(t))) out.push(links[i].closest('p') || links[i]);
    }
    return out;
  }

  function pruneKolNav() {
    if (!S.run) return;
    var root = kolRoot();
    if (!root) {
      kolNavItems().forEach(function (p) { if (p.parentNode) p.parentNode.removeChild(p); });
      return;
    }
    // Instafeed row: the item stays as rendered (hiding it while the feed loads
    // made the nav jump twice). Only a feed still empty after 10s (no posts, or
    // Instafeed still processing it) loses the item, so a jump is rare.
    if (!root.hasAttribute('data-cx-ugc-app')) return;
    setTimeout(function () {
      if (root.querySelector('.instafeed-new-layout-item')) return;
      kolNavItems().forEach(function (p) { if (p.parentNode) p.parentNode.removeChild(p); });
    }, 10000);
  }

  /* ------------------------------------------------------------------ boot */
  function init() {
    var tabsEl = document.querySelector('[data-cx-tabs]');
    if (!tabsEl) return;
    S.root = tabsEl;
    S.bar = tabsEl.querySelector('.cx-tabs__bar');
    S.endWrap = document.querySelector('[data-cx-tabs-end]');
    S.toggle = S.endWrap ? S.endWrap.querySelector('[data-cx-desc-toggle]') : null;
    S.design = tabsEl.classList.contains('cx-tabs--design');
    S.run = findRun();
    if (!S.run) return;

    // 產品規格: the run sections whose keys the patch wrote to the tab host
    var keys = (tabsEl.getAttribute('data-cx-specs') || '').split(',');
    S.specs = [];
    for (var q = 0; q < S.run.secs.length; q++) {
      for (var kk = 0; kk < keys.length; kk++) {
        var key = keys[kk].replace(/^\s+|\s+$/g, '');
        var sid = S.run.secs[q].id || '';
        if (key && sid.slice(-(key.length + 2)) === '__' + key) { S.specs.push(S.run.secs[q]); break; }
      }
    }

    // aria-controls: the description tab controls the other run sections, the specs tab the spec sections
    var dTab = tabsEl.querySelector('[data-cx-tab="description"]');
    var sTab = tabsEl.querySelector('[data-cx-tab="specs"]');
    var ids = [], sids = [];
    for (var i = 0; i < S.run.secs.length; i++) {
      if (!S.run.secs[i].id) continue;
      if (isSpecSec(S.run.secs[i])) sids.push(S.run.secs[i].id); else ids.push(S.run.secs[i].id);
    }
    if (dTab && ids.length) dTab.setAttribute('aria-controls', ids.join(' '));
    if (sTab && sids.length) sTab.setAttribute('aria-controls', sids.join(' '));

    if (!tabsEl.getAttribute('data-cx-bound')) {
      tabsEl.setAttribute('data-cx-bound', '1');
      var tabs = tabButtons();
      tabs.forEach(function (t) {
        t.addEventListener('click', function () { select(t.getAttribute('data-cx-tab')); });
        t.addEventListener('keydown', onTabKey);
      });
      var pts = tabsEl.querySelectorAll('[data-cx-policy-toggle]');
      Array.prototype.forEach.call(pts, function (b) {
        b.addEventListener('click', function () { onPolicyToggle(b); });
      });
    }
    if (S.toggle && !S.toggle.getAttribute('data-cx-bound')) {
      S.toggle.setAttribute('data-cx-bound', '1');
      S.toggle.addEventListener('click', function () { if (isOpen()) collapse(true); else expand(true); });
    }

    initKol();
    pruneKolNav();

    if (S.design) {
      root.classList.add('cx-tabs-design', 'cx-tabs-ready');
      return;
    }
    layout();
    setToggleLabel();
    prefetchShipping();
    scrollToHash();
  }

  window.ConvxTabs = { reveal: reveal };

  if (!bound.global) {
    bound.global = true;
    window.addEventListener('message', onMessage);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('load', pruneKolNav);
    window.addEventListener('hashchange', scrollToHash);
    window.addEventListener('load', function () {
      if (S.root && isAlt()) {
        var p = S.root.querySelector('[data-cx-panel="' + currentTab() + '"]');
        if (p) layoutPolicy(p);
      }
    });
    var rsz = null, lastW = window.innerWidth;
    window.addEventListener('resize', function () {
      if (rsz) return;
      rsz = window.requestAnimationFrame(function () {
        rsz = null;
        if (window.innerWidth !== lastW) { lastW = window.innerWidth; layout(); }
      });
    });
    // theme editor: a reloaded section brings fresh markup
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector && (e.target.querySelector('[data-cx-tabs]') || e.target.querySelector('[data-cx-tabs-end]'))) init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
